// ImpersonationContext.tsx
// Allows an admin to view the application as a specific regular user (read-only by default,
// with an explicit write-unlock step). State is persisted in sessionStorage so it survives
// page refreshes but is cleared when the browser tab is closed.
// Updated Jun 2026: added currentBannerHeight (28px fixed strip when impersonating, 0 otherwise).
// Updated Jun 2026: startImpersonation returns boolean; deduplicated loadFromSession init; added shape validation.

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';

/** Height of the impersonation banner strip in pixels. */
export const BANNER_HEIGHT = 28;

export interface ImpersonatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  initials: string;
}

interface ImpersonationContextType {
  impersonatedUser: ImpersonatedUser | null;
  isImpersonating: boolean;
  isWriteUnlocked: boolean;
  currentBannerHeight: number;
  effectiveUserId: string | null;
  startImpersonation: (userId: string) => Promise<boolean>;
  stopImpersonation: () => void;
  toggleWriteUnlock: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const SESSION_KEY = 'qollect_impersonation';

function buildDisplayName(firstName: string, lastName: string, email: string): { displayName: string; initials: string } {
  if (firstName || lastName) {
    const displayName = [firstName, lastName].filter(Boolean).join(' ');
    const parts = displayName.split(' ').filter(Boolean);
    const initials = parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : displayName.slice(0, 2).toUpperCase();
    return { displayName, initials };
  }
  const prefix = email.split('@')[0];
  return {
    displayName: prefix.charAt(0).toUpperCase() + prefix.slice(1),
    initials: prefix.slice(0, 2).toUpperCase(),
  };
}

interface SessionData {
  user: ImpersonatedUser;
  writeUnlocked: boolean;
}

function loadFromSession(): SessionData | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.user?.id !== 'string' ||
      typeof parsed?.user?.email !== 'string' ||
      typeof parsed?.writeUnlocked !== 'boolean'
    ) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed as SessionData;
  } catch {
    return null;
  }
}

function saveToSession(user: ImpersonatedUser, writeUnlocked: boolean): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user, writeUnlocked }));
  } catch {
    // sessionStorage unavailable — state is in-memory only
  }
}

function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

interface ImpersonationProviderProps {
  children: React.ReactNode;
  realUserId: string | null;
}

export function ImpersonationProvider({ children, realUserId }: ImpersonationProviderProps) {
  const initialSession = loadFromSession();
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(
    initialSession?.user ?? null
  );
  const [isWriteUnlocked, setIsWriteUnlocked] = useState<boolean>(
    initialSession?.writeUnlocked ?? false
  );

  // If the real user changes (e.g., different admin logs in), clear any stale impersonation.
  useEffect(() => {
    if (!realUserId) {
      setImpersonatedUser(null);
      setIsWriteUnlocked(false);
      clearSession();
    }
  }, [realUserId]);

  const startImpersonation = useCallback(async (userId: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) {
      console.error('[ImpersonationContext] Failed to load target user profile', error);
      return false;
    }

    const firstName = data.first_name?.trim() ?? '';
    const lastName = data.last_name?.trim() ?? '';
    const { displayName, initials } = buildDisplayName(firstName, lastName, data.email);

    const user: ImpersonatedUser = {
      id: data.id,
      email: data.email,
      firstName,
      lastName,
      displayName,
      initials,
    };

    setImpersonatedUser(user);
    setIsWriteUnlocked(false);
    saveToSession(user, false);
    return true;
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedUser(null);
    setIsWriteUnlocked(false);
    clearSession();
  }, []);

  const toggleWriteUnlock = useCallback(() => {
    setIsWriteUnlocked((prev) => {
      const next = !prev;
      if (impersonatedUser) saveToSession(impersonatedUser, next);
      return next;
    });
  }, [impersonatedUser]);

  const isImpersonating = impersonatedUser !== null;

  const value: ImpersonationContextType = {
    impersonatedUser,
    isImpersonating,
    isWriteUnlocked,
    currentBannerHeight: isImpersonating ? BANNER_HEIGHT : 0,
    effectiveUserId: impersonatedUser?.id ?? realUserId,
    startImpersonation,
    stopImpersonation,
    toggleWriteUnlock,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}
